import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, Share2, ChevronRight } from 'lucide-react';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { CTASection } from '../components/home/CTASection';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Placeholder post content
const placeholderPosts = {
  'como-empezar-dieta-natural-perro': {
    id: '1',
    slug: 'como-empezar-dieta-natural-perro',
    title: 'Cómo empezar con la dieta natural para tu perro: guía para principiantes',
    excerpt: 'Todo lo que necesitas saber para dar los primeros pasos hacia una alimentación más saludable para tu perro.',
    category: 'dieta-perros',
    date: '2024-01-15',
    readTime: '8 min',
    image: 'https://images.unsplash.com/photo-1692906456159-f93a6f3df407?w=1200',
    content: `
## ¿Por qué considerar la dieta natural?

La alimentación natural para perros se basa en ingredientes frescos y mínimamente procesados. A diferencia de los piensos comerciales, ofrece nutrientes en su forma más biodisponible.

## Primeros pasos

Antes de empezar, es importante:

1. **Consultar con un profesional**: Cada perro tiene necesidades diferentes.
2. **Hacer una transición gradual**: El cambio brusco puede causar problemas digestivos.
3. **Planificar los menús**: Una dieta natural debe estar equilibrada.

## Ingredientes básicos

Una dieta natural típica incluye:

- **Proteína animal**: Pollo, ternera, pavo, pescado (70-80%)
- **Huesos carnosos**: Fuente de calcio y fósforo
- **Vísceras**: Hígado, corazón, riñón (10-15%)
- **Vegetales**: Calabacín, zanahoria, espinacas (5-10%)

## Errores comunes a evitar

- Dar solo músculo sin vísceras ni huesos
- No calcular las cantidades correctamente
- Olvidar los suplementos necesarios
- Cambiar la dieta de golpe

## Conclusión

Empezar con la dieta natural requiere información y planificación, pero los beneficios pueden ser significativos. Si tienes dudas, consulta con un profesional que pueda guiarte.
    `,
    faqs: [
      {
        question: '¿A qué edad puedo empezar con dieta natural?',
        answer: 'Se puede empezar a cualquier edad, pero los cachorros tienen necesidades específicas que deben supervisarse.',
      },
      {
        question: '¿Es más cara que el pienso?',
        answer: 'Depende de los ingredientes y la calidad. Puede ser similar o algo más costosa, pero muchas familias lo compensan con mejor salud.',
      },
    ],
    relatedPosts: ['gatos-selectivos-alimentacion-natural', 'calcular-porciones-comida-natural'],
  },
  'gatos-selectivos-alimentacion-natural': {
    id: '2',
    slug: 'gatos-selectivos-alimentacion-natural',
    title: 'Alimentación natural para gatos selectivos: estrategias que funcionan',
    excerpt: 'Los gatos pueden ser muy exigentes con la comida. Descubre cómo introducir alimentos naturales sin frustrarte.',
    category: 'dieta-gatos',
    date: '2024-01-10',
    readTime: '6 min',
    image: 'https://images.unsplash.com/photo-1668979323445-11d84f1be907?w=1200',
    content: `
## El reto de los gatos selectivos

Los gatos son conocidos por ser quisquillosos con la comida. A diferencia de los perros, su instinto les hace desconfiar de alimentos nuevos.

## Estrategias efectivas

### 1. Paciencia ante todo
No fuerces. Un gato estresado rechazará más la comida.

### 2. Temperatura correcta
Los gatos prefieren la comida a temperatura ambiente o ligeramente tibia.

### 3. Textura adecuada
Experimenta con texturas: algunos prefieren trozos, otros picado fino.

### 4. Mezcla gradual
Empieza mezclando una pequeña cantidad con su comida habitual.

## Alimentos que suelen gustar

- Pollo cocido (sin piel ni huesos)
- Pavo
- Pescado blanco
- Yema de huevo

## Conclusión

La clave con los gatos es la paciencia y la consistencia. No todos aceptarán la dieta natural, y está bien respetar sus preferencias.
    `,
    faqs: [
      {
        question: '¿Qué hago si mi gato no acepta nada nuevo?',
        answer: 'Algunos gatos necesitan semanas o meses de exposición gradual. Si persiste el rechazo, quizás no sea el momento adecuado.',
      },
    ],
    relatedPosts: ['como-empezar-dieta-natural-perro'],
  },
  'calcular-porciones-comida-natural': {
    id: '3',
    slug: 'calcular-porciones-comida-natural',
    title: 'Cómo calcular las porciones de comida natural para tu mascota',
    excerpt: 'Una de las dudas más frecuentes: ¿cuánta comida natural debo dar? Te explico los factores a considerar.',
    category: 'porciones',
    date: '2024-01-05',
    readTime: '7 min',
    image: 'https://images.unsplash.com/photo-1602470521011-4b18a8ea292b?w=1200',
    content: `
## La pregunta del millón

"¿Cuánto le doy?" es probablemente la pregunta más frecuente cuando se empieza con dieta natural. Y la respuesta es: depende.

## Factores que influyen

1. **Peso actual y objetivo**
2. **Nivel de actividad**
3. **Edad y metabolismo**
4. **Estado de salud**
5. **Raza y tamaño**

## Fórmula orientativa

Como punto de partida, se suele calcular:

- **Perros adultos**: 2-3% de su peso corporal al día
- **Cachorros**: 5-10% (según edad)
- **Gatos**: 2-4% de su peso corporal

## Ejemplo práctico

Un perro adulto de 20 kg con actividad moderada:
- 2.5% de 20 kg = 500 g de comida al día

## Ajustes según evolución

La cantidad inicial es orientativa. Deberás ajustar según:
- ¿Está ganando o perdiendo peso?
- ¿Tiene energía suficiente?
- ¿Las heces son normales?

## Conclusión

No hay una fórmula mágica. El cálculo inicial es un punto de partida que se ajusta con la observación.
    `,
    faqs: [
      {
        question: '¿Debo pesar la comida siempre?',
        answer: 'Al principio sí, para acostumbrarte a las cantidades. Con el tiempo podrás calcular a ojo.',
      },
    ],
    relatedPosts: ['como-empezar-dieta-natural-perro'],
  },
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/blog/posts/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
        } else {
          // Use placeholder
          setPost(placeholderPosts[slug] || null);
        }
      } catch (error) {
        setPost(placeholderPosts[slug] || null);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="section-container pt-12 pb-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-brand-sage/30 rounded w-3/4" />
          <div className="h-4 bg-brand-sage/30 rounded w-1/2" />
          <div className="h-64 bg-brand-sage/30 rounded" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="section-container pt-12 pb-20 text-center">
        <h1 className="font-manrope text-3xl font-bold text-text-primary mb-4">
          Artículo no encontrado
        </h1>
        <Link to="/blog" className="text-brand-primary hover:underline">
          Volver al blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${post.title} | REALimenta Blog`}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        image={post.image}
        type="article"
      />

      <article className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[
            { href: '/blog', label: 'Blog' },
            { label: post.title }
          ]} />

          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-text-muted hover:text-brand-primary mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al blog
          </Link>

          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mb-8"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="flex items-center gap-1 text-sm text-text-muted">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-1 text-sm text-text-muted">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>

            <h1 
              className="font-manrope text-3xl sm:text-4xl lg:text-5xl font-extrabold text-text-primary tracking-tighter mb-6"
              data-testid="blog-post-h1"
            >
              {post.title}
            </h1>

            <p className="text-text-secondary text-lg">
              {post.excerpt}
            </p>
          </motion.header>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mb-12"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full aspect-video object-cover rounded-3xl shadow-brand"
            />
          </motion.div>

          {/* Content - Sanitized with DOMPurify to prevent XSS */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-3xl mx-auto prose prose-lg prose-headings:font-manrope prose-headings:text-text-primary prose-p:text-text-secondary prose-li:text-text-secondary prose-strong:text-text-primary"
            dangerouslySetInnerHTML={{ 
              __html: DOMPurify.sanitize(
                post.content
                  .replace(/\n/g, '<br />')
                  .replace(/## /g, '<h2>')
                  .replace(/### /g, '<h3>')
                  .replace(/<h2>/g, '</p><h2>')
                  .replace(/<h3>/g, '</p><h3>')
                  .replace(/<\/h2>\n/g, '</h2><p>')
                  .replace(/<\/h3>\n/g, '</h3><p>')
              )
            }}
            data-testid="blog-post-content"
          />

          {/* FAQs */}
          {post.faqs && post.faqs.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto mt-16"
            >
              <h2 className="font-manrope text-2xl font-bold text-text-primary mb-6">
                Preguntas frecuentes
              </h2>
              <div className="space-y-4">
                {post.faqs.map((faq, index) => (
                  <div key={faq.question || `faq-${index}`} className="card-brand">
                    <h3 className="font-semibold text-text-primary mb-2">{faq.question}</h3>
                    <p className="text-text-secondary text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {/* Share */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto mt-12 flex items-center justify-between p-6 bg-brand-sage/20 rounded-2xl"
          >
            <span className="text-text-secondary">¿Te ha resultado útil?</span>
            <button className="inline-flex items-center gap-2 text-brand-primary font-semibold">
              <Share2 className="w-4 h-4" />
              Compartir
            </button>
          </motion.div>

          {/* Related posts */}
          {post.relatedPosts && post.relatedPosts.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto mt-16"
            >
              <h2 className="font-manrope text-2xl font-bold text-text-primary mb-6">
                Artículos relacionados
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {post.relatedPosts.map((relatedSlug) => {
                  const relatedPost = placeholderPosts[relatedSlug];
                  if (!relatedPost) return null;
                  return (
                    <Link
                      key={relatedSlug}
                      to={`/blog/${relatedSlug}`}
                      className="card-brand group"
                    >
                      <h3 className="font-manrope font-bold text-text-primary group-hover:text-brand-primary transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-text-secondary text-sm mt-2 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </motion.section>
          )}
        </div>
      </article>

      <CTASection />
    </>
  );
};

export default BlogPostPage;
